/**
 * TEMPORARY — diagnostics for DCD-7000 playback bring-up. Delete this file and
 * its call sites once the player is confirmed working against a real stream.
 *
 * It exists because the failure mode we are chasing is silent: the Brightcove
 * loader resolves through a promise, so anything that throws inside its `then`
 * is swallowed, and a player that never attaches looks identical to one that is
 * merely buffering. Every line here answers one question — did the loader
 * settle, did the engine hear about it, and what status is the chrome drawing.
 *
 * Lines are also buffered so the whole sequence can be copied in one go:
 *
 *     copy(__playerLog())      // Chrome/Edge — straight to the clipboard
 *     __playerLog()            // anywhere else — returns one string
 *
 * That matters because the console collapses repeats and truncates objects, and
 * a half-copied sequence is worse than none: it looks like the run stopped where
 * the paste stopped.
 *
 * On by default in `vite dev`. To turn it on against a preview or deployed
 * build, run `localStorage.setItem("player:debug", "1")` and reload.
 */

const isEnabled = (): boolean => {
  if (import.meta.env.DEV) {
    return true
  }

  // Storage access throws outright in some embedded and privacy contexts, and a
  // logger must never be the thing that breaks the page.
  try {
    return globalThis.localStorage?.getItem("player:debug") === "1"
  } catch {
    return false
  }
}

const enabled = isEnabled()

const started = Date.now()

/** Enough for a full session; old lines are dropped rather than growing forever. */
const MAX_LINES = 500

const buffer: string[] = []

/** Milliseconds since load, so ordering and stalls are readable at a glance. */
const stamp = (): string => `+${String(Date.now() - started).padStart(6, " ")}ms`

/**
 * Detail can be a DOM node, an Error, or a Video.js object with circular
 * references, so plain `JSON.stringify` is not safe here.
 */
const describe = (detail: unknown): string => {
  if (detail === undefined) {
    return ""
  }

  if (detail instanceof Error) {
    return ` ${detail.name}: ${detail.message}`
  }

  try {
    return ` ${JSON.stringify(detail)}`
  } catch {
    return ` ${String(detail)}`
  }
}

const record = (level: "info" | "warn", message: string, detail?: unknown): void => {
  if (!enabled) {
    return
  }

  buffer.push(`[player] ${stamp()} ${message}${describe(detail)}`)

  if (buffer.length > MAX_LINES) {
    buffer.shift()
  }

  if (detail === undefined) {
    console[level](`[player] ${stamp()} ${message}`)
    return
  }

  console[level](`[player] ${stamp()} ${message}`, detail)
}

export const playerLog = (message: string, detail?: unknown): void => record("info", message, detail)

export const playerWarn = (message: string, detail?: unknown): void => record("warn", message, detail)

if (enabled) {
  ;(globalThis as { __playerLog?: () => string }).__playerLog = () =>
    buffer.length > 0 ? buffer.join("\n") : "[player] no entries — the player feature never mounted"

  /**
   * Uncaught errors land in the buffer too, and they are the point.
   *
   * `player-loader` calls `initPlayer` from the injected script's `onload`, and
   * its first statement dereferences `window.bc` before the guard that would
   * reject:
   *
   *     var bc = window.bc[playerId + "_" + embedId] || window.bc
   *     if (!bc) { return reject(...) }
   *
   * A throw inside an event handler cannot reject the surrounding promise, so
   * that failure is invisible to `.catch` — the promise simply stays pending and
   * the player hangs. The only trace is an uncaught error, which is easy to miss
   * when reading a filtered console. Capturing it here puts it in the same
   * ordered buffer as everything else.
   */
  if (typeof window !== "undefined") {
    window.addEventListener("error", event => {
      record("warn", "UNCAUGHT", event.error instanceof Error ? event.error : event.message)
    })

    window.addEventListener("unhandledrejection", event => {
      record("warn", "UNHANDLED REJECTION", event.reason instanceof Error ? event.reason : String(event.reason))
    })
  }
}
